import { useState, useMemo, useRef, useLayoutEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Info, ExternalLink, File } from 'lucide-react'

import CEFRBadge from '../components/shared/CEFRBadge'
import { toast } from '../components/shared/Toast'
import { extractErrorMessage } from '../api/client'
import { useCreateDeckFromText, useDismissWords } from '../hooks/useTextImport'
import { useLocale } from '../i18n/context'
import { getAppScrollElement } from '../utils/appScroll'
import { clearTextImportFlow } from '../utils/textImportFlowState'
import type { TextImportExtractResponse, WordCandidateResponse, ConfirmedWord } from '../types'

interface LocationState {
  extractResponse: TextImportExtractResponse
  title: string | null
  targetDeckId?: string | null
  targetDeckTitle?: string | null
}

export default function WordReviewPage() {
  const { tr } = useLocale()
  const wr = tr.wordReview
  const { id: textImportId } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  const state = location.state as LocationState | null

  // ── Guard: navigated here without extract data ───────────────────────────────
  if (!state?.extractResponse || !textImportId) {
    return (
      <div
        className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center gap-4 px-4"
        style={{ color: 'var(--text-primary)' }}
      >
        <Info size={40} style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
        <p className="text-base font-medium" style={{ color: 'var(--text-secondary)' }}>
          {wr.noDataFound}
        </p>
        <button
          onClick={() => navigate('/text-import')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95"
          style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
        >
          {wr.goToImport}
        </button>
      </div>
    )
  }

  // The review is its own component so that its hooks run on every render of
  // it. They used to sit after the guard above, so a history step that
  // changed `location.state` on the same URL (guard ↔ review) changed the
  // hook count between renders and React threw.
  return <WordReview textImportId={textImportId} state={state} />
}

function WordReview({ textImportId, state }: { textImportId: string; state: LocationState }) {
  const { tr } = useLocale()
  const wr = tr.wordReview
  const navigate = useNavigate()
  const createDeck = useCreateDeckFromText()
  const dismissWords = useDismissWords()

  const [addToQueue, setAddToQueue] = useState(false)

  // `.wr-footer` is `position: fixed`, which places it against the browser
  // viewport — but on desktop the app's content column sits to the right of
  // the sidebar (Layout.tsx), so a viewport-relative `left:0; right:0` footer
  // centers its content under the *whole window*, not under the narrower
  // column the rest of the page is centered in. The mismatch reads as the
  // button sitting shifted left of where the page above it is centered, and
  // on a narrow-enough window the shift can carry it partly behind the
  // (higher z-index, opaque) sidebar. Mirroring `<main>`'s own rect onto the
  // footer's left/width — the one shared scroll surface, ADR-067 Decision 3
  // — keeps it aligned regardless of sidebar collapse state or window size,
  // without WordReviewPage needing to know anything about the sidebar itself.
  const footerRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const mainEl = getAppScrollElement()
    const footerEl = footerRef.current
    if (!mainEl || !footerEl) return
    function sync() {
      const rect = mainEl!.getBoundingClientRect()
      footerEl!.style.left = `${rect.left}px`
      footerEl!.style.width = `${rect.width}px`
    }
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(mainEl)
    window.addEventListener('resize', sync)
    return () => { ro.disconnect(); window.removeEventListener('resize', sync) }
  }, [])

  const { extractResponse, title } = state

  // A real back-navigation (unwinds the history stack by one entry) rather
  // than a push to '/text-import' — pushing here was what made the in-page
  // control diverge from the browser/Android back gesture: it grew history
  // instead of unwinding it, and landed on a *different* entry than the one
  // the system gesture returns to, so only the in-page path carried a
  // restorable draft. Both now land on the exact same entry, and step 1
  // restores its draft from frontend/src/utils/textImportFlowState.ts by
  // matching that entry's location.key — not from anything passed here.
  function backToStep1() {
    navigate(-1)
  }
  const {
    candidates,
    groups,
    language,
    total_found,
    already_known,
    below_level: belowLevelCount,
    new_words,
    words_total,
    words_known,
    source_url,
    source_title,
    subtitle_auto_generated,
    source_filename,
    user_level,
  } = extractResponse

  const summaryFound = total_found ?? words_total ?? 0
  const summaryKnown = already_known ?? words_known ?? 0
  const summaryBelow = belowLevelCount ?? 0
  const summaryNew = new_words ?? 0

  const useGroups = user_level != null

  // ── Deduplicate by lemma ─────────────────────────────────────────────────────
  const uniqueCandidates = useMemo<WordCandidateResponse[]>(() => {
    const seen = new Set<string>()
    return candidates.filter((w) => {
      if (seen.has(w.lemma)) return false
      seen.add(w.lemma)
      return true
    })
  }, [candidates])

  // ── The words that are not candidates (collapsed, unselected, selectable) ────
  // Each distinct word is in exactly one list on the server; keyed by lemma here
  // like everything else on this screen, so a repeat could never be counted twice.
  const { knownWords, studyingWords, functionWords, nameWords } = useMemo(() => {
    const seen = new Set(uniqueCandidates.map((w) => w.lemma))
    const fresh = (list: WordCandidateResponse[] | undefined) =>
      (list ?? []).filter((w) => {
        if (seen.has(w.lemma)) return false
        seen.add(w.lemma)
        return true
      })
    return {
      knownWords: fresh(groups?.already_known),
      studyingWords: fresh(groups?.already_studying),
      functionWords: fresh(groups?.function_words),
      nameWords: fresh(groups?.names),
    }
  }, [groups, uniqueCandidates])
  const groupWords = useMemo(
    () => [...knownWords, ...studyingWords, ...functionWords, ...nameWords],
    [knownWords, studyingWords, functionWords, nameWords],
  )
  // A studying word whose card is already in the destination deck cannot be added
  // again: it is shown, disabled, and never selected. The destination is known here,
  // not at extraction, so the server lists the decks and this compares.
  const destinationDeckId = state?.targetDeckId ?? null
  const inDestination = (w: WordCandidateResponse) =>
    destinationDeckId != null && (w.deck_ids ?? []).includes(destinationDeckId)
  const studyingRowState = (w: WordCandidateResponse): RowState =>
    inDestination(w)
      ? { disabled: true, note: wr.studyingInThisDeck }
      : { disabled: false, note: wr.studyingSecondCard }
  // Everything the learner can select: the candidates and the words in the groups.
  const allWords = useMemo(() => [...uniqueCandidates, ...groupWords], [uniqueCandidates, groupWords])

  // ── Group words ──────────────────────────────────────────────────────────────
  const { recommendedWords, advancedWords, likelyKnownWords, mainWords, belowLevelWords } =
    useMemo(() => {
      if (useGroups) {
        const recommended: WordCandidateResponse[] = []
        const advanced: WordCandidateResponse[] = []
        const likelyKnown: WordCandidateResponse[] = []
        for (const w of uniqueCandidates) {
          const g = w.relevance_group ?? 'recommended'
          if (g === 'advanced') advanced.push(w)
          else if (g === 'likely_known') likelyKnown.push(w)
          else recommended.push(w)
        }
        return { recommendedWords: recommended, advancedWords: advanced, likelyKnownWords: likelyKnown, mainWords: [], belowLevelWords: [] }
      }
      return {
        recommendedWords: [], advancedWords: [], likelyKnownWords: [],
        mainWords: uniqueCandidates.filter((w) => !w.below_level),
        belowLevelWords: uniqueCandidates.filter((w) => w.below_level),
      }
    }, [uniqueCandidates, useGroups])

  // ── Checkbox state ───────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(() => {
    if (useGroups) {
      return new Set([
        ...recommendedWords.map((w) => w.lemma),
        ...advancedWords.map((w) => w.lemma),
      ])
    }
    return new Set(mainWords.map((w) => w.lemma))
  })

  const primaryWords = useGroups ? recommendedWords : mainWords
  const allPrimarySelected =
    primaryWords.length > 0 && primaryWords.every((w) => selected.has(w.lemma))
  const somePrimarySelected =
    primaryWords.some((w) => selected.has(w.lemma)) && !allPrimarySelected

  function toggleWord(lemma: string) {
    if (studyingWords.some((w) => w.lemma === lemma && inDestination(w))) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(lemma)) next.delete(lemma)
      else next.add(lemma)
      return next
    })
  }

  function toggleAll() {
    if (allPrimarySelected) {
      setSelected((prev) => {
        const next = new Set(prev)
        primaryWords.forEach((w) => next.delete(w.lemma))
        return next
      })
    } else {
      setSelected((prev) => {
        const next = new Set(prev)
        primaryWords.forEach((w) => next.add(w.lemma))
        return next
      })
    }
  }

  const selectedCount = useMemo(
    () => allWords.filter((w) => selected.has(w.lemma)).length,
    [allWords, selected],
  )
  // "N of M selected": M is the candidates plus any group word the learner picked,
  // so N never exceeds M and the figure is the candidates' own until they pick one.
  const selectedFromGroups = useMemo(
    () => groupWords.filter((w) => selected.has(w.lemma)).length,
    [groupWords, selected],
  )

  // ── Confirm handler ──────────────────────────────────────────────────────────
  function handleConfirm() {
    if (selectedCount === 0) {
      toast.error(wr.selectAtLeastOne)
      return
    }

    // A word picked from a group is confirmed exactly like a candidate.
    const confirmedWords: ConfirmedWord[] = allWords
      .filter((w) => selected.has(w.lemma))
      .map((w) => ({
        word: w.word,
        lemma: w.lemma,
        pos: w.pos,
        sentence: w.sentence,
        cefr_level: w.cefr_level,
      }))

    // Only recommended candidates the learner passed over are dismissed ("I know
    // this"). Words in the groups are never dismissed by being left unselected.
    const dismissSource = useGroups ? recommendedWords : mainWords
    const dismissedLemmas = dismissSource
      .filter((w) => !selected.has(w.lemma))
      .map((w) => ({ lemma: w.lemma, language }))

    if (dismissedLemmas.length > 0) {
      dismissWords.mutate({ lemmas: dismissedLemmas })
    }

    const targetDeckId = state?.targetDeckId ?? null
    createDeck.mutate(
      {
        text_import_id: textImportId,
        confirmed_words: confirmedWords,
        title,
        target_deck_id: targetDeckId,
        ...(targetDeckId == null ? { is_active_for_learning: addToQueue } : {}),
      },
      {
        onSuccess: (data) => {
          // Flow completed — the draft belongs to an import that no longer
          // exists in draft form, so a later import must not open pre-filled.
          clearTextImportFlow()
          navigate(`/text-import/${data.text_import_id}/status`, {
            replace: true,
            state: {
              deck_id: data.deck_id,
              title,
              words_skipped: data.words_skipped,
              target_deck_title: state?.targetDeckTitle ?? null,
            },
          })
        },
        onError: (err: unknown) => {
          toast.error(extractErrorMessage(err, 'Could not create your deck. Please try again.'))
        },
      },
    )
  }

  // ── Empty state: the text had no words at all ────────────────────────────────
  // A text whose every word is known, a function word or a name still has a screen:
  // those words are in the groups below, where the learner can select them.
  if (allWords.length === 0) {
    return (
      <div
        className="min-h-[calc(100vh-64px)] flex flex-col items-center px-4 py-10"
        style={{ color: 'var(--text-primary)' }}
      >
        <PageHeader title={title} sourceUrl={source_url} sourceTitle={source_title} sourceFilename={source_filename} onBack={backToStep1} />
        <div
          className="w-full max-w-2xl flex flex-col items-center justify-center gap-4 py-20 rounded-2xl mt-4"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}
        >
          <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {wr.noNewWordsTitle}
          </p>
          <p className="text-sm text-center max-w-xs" style={{ color: 'var(--text-secondary)' }}>
            {summaryKnown > 0 ? wr.allWordsKnown(summaryKnown) : wr.noVocabExtracted}
          </p>
          <button
            onClick={() => navigate('/text-import')}
            className="mt-2 flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95"
            style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
          >
            {wr.importAnother}
          </button>
        </div>
      </div>
    )
  }

  // ── Main view ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Mobile bottom clearance so last row is never hidden by footer */}
      <style>{`
        @media (max-width: 767px) {
          .wr-content { padding-bottom: 140px; }
          .wr-footer {
            position: fixed;
            bottom: calc(60px + env(safe-area-inset-bottom, 0px));
            left: 0; right: 0;
          }
        }
        @media (min-width: 768px) {
          .wr-content { padding-bottom: 100px; }
          .wr-footer { position: fixed; bottom: 0; left: 0; right: 0; }
        }
      `}</style>

      <div
        className="wr-content flex flex-col items-center px-4 pt-8"
        style={{ color: 'var(--text-primary)' }}
      >
        <div className="w-full max-w-2xl flex flex-col gap-5">
          <PageHeader title={title} sourceUrl={source_url} sourceTitle={source_title} sourceFilename={source_filename} onBack={backToStep1} />

          {/* ── 2×2 Stats grid ──────────────────────────────────────────────── */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{ border: '1px solid var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-card)' }}
          >
            <div className="grid grid-cols-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <StatCell value={summaryFound} label={wr.statFound} accent />
              <StatCell value={summaryKnown} label={wr.statKnown} bordered muted />
            </div>
            <div className="grid grid-cols-2">
              <StatCell value={summaryBelow} label={wr.statBelow} muted />
              <StatCell value={summaryNew} label={wr.statNew} bordered />
            </div>
          </div>

          {/* ── "Ranked for your level [pill]" ──────────────────────────────── */}
          {useGroups && user_level && (
            <div className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {wr.rankedForLevel}
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                height: 22, padding: '0 9px',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--accent-fill)', color: 'var(--text-on-accent)',
                fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
                lineHeight: 1,
              }}>
                {user_level}
              </span>
            </div>
          )}

          {/* Auto-generated captions warning */}
          {subtitle_auto_generated === true && (
            <div
              className="flex items-start gap-2.5 px-4 py-3 rounded-xl"
              style={{
                fontSize: 14,
                background: 'var(--warning-soft)',
                border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)',
                color: 'var(--text-primary)',
              }}
            >
              <Info size={15} className="shrink-0 mt-0.5" style={{ color: 'var(--warning)' }} />
              <span>{wr.autoCaptionsWarn}</span>
            </div>
          )}

          {/* Nothing recommended, but the text has words: say so; the groups follow. */}
          {uniqueCandidates.length === 0 && (
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>
              {wr.noNewWordsTitle}
            </p>
          )}

          {/* ── Master selection bar ─────────────────────────────────────────── */}
          {primaryWords.length > 0 && (
            <div
              className="flex items-center"
              style={{
                gap: 12, padding: '10px 12px',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {/* Master checkbox — filled / indeterminate / empty */}
              <button
                type="button"
                onClick={toggleAll}
                aria-label={allPrimarySelected ? wr.deselectAll : wr.selectAll}
                style={{
                  width: 24, height: 24, borderRadius: 7, flexShrink: 0,
                  border: `2px solid ${allPrimarySelected || somePrimarySelected ? 'var(--accent-fill)' : 'var(--border)'}`,
                  background: allPrimarySelected || somePrimarySelected ? 'var(--accent-fill)' : 'var(--bg-surface)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'background 120ms, border-color 120ms',
                }}
              >
                {allPrimarySelected ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 6" stroke="white" strokeWidth="3.2" />
                  </svg>
                ) : somePrimarySelected ? (
                  <span style={{ display: 'block', width: 11, height: 3, borderRadius: 2, background: 'white' }} />
                ) : null}
              </button>

              <span className="flex-1" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {wr.selectedCount(selectedCount, uniqueCandidates.length + selectedFromGroups)}
              </span>

              <button
                type="button"
                onClick={toggleAll}
                className="transition-opacity hover:opacity-70"
                style={{ fontSize: 14, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: '6px 4px', minHeight: 32 }}
              >
                {allPrimarySelected ? wr.deselectAll : wr.selectAll}
              </button>
            </div>
          )}

          {useGroups ? (
            // ── Three-group layout ────────────────────────────────────────────
            <>
              {recommendedWords.length > 0 && (
                <WordSection title={wr.groupRecommended} count={recommendedWords.length} words={recommendedWords} selected={selected} onToggle={toggleWord} dimLevel={0} />
              )}
              {advancedWords.length > 0 && (
                <WordSection title={wr.groupAdvanced} count={advancedWords.length} words={advancedWords} selected={selected} onToggle={toggleWord} dimLevel={1} />
              )}
              {likelyKnownWords.length > 0 && (
                <WordSection title={wr.groupLikelyKnown} count={likelyKnownWords.length} words={likelyKnownWords} selected={selected} onToggle={toggleWord} dimLevel={2} />
              )}
            </>
          ) : (
            // ── Legacy two-section layout ────────────────────────────────────
            <>
              {mainWords.length > 0 && (
                <ul className="flex flex-col rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}>
                  {mainWords.map((word, idx) => (
                    <WordRow key={word.lemma} word={word} isChecked={selected.has(word.lemma)} isLast={idx === mainWords.length - 1} onToggle={() => toggleWord(word.lemma)} />
                  ))}
                </ul>
              )}
              {belowLevelWords.length > 0 && (
                <BelowLevelGroup words={belowLevelWords} selected={selected} onToggle={toggleWord} label={wr.belowLevelCount(belowLevelWords.length)} />
              )}
            </>
          )}

          {/* ── The rest of the text's words: collapsed, unselected, selectable ───
              Reuses the screen's one collapsible. Nothing here is dropped or
              dismissed; opening a group and ticking a word adds it to the deck. */}
          {knownWords.length > 0 && (
            <BelowLevelGroup words={knownWords} selected={selected} onToggle={toggleWord} label={`${wr.groupKnown} · ${knownWords.length}`} />
          )}
          {studyingWords.length > 0 && (
            <BelowLevelGroup words={studyingWords} selected={selected} onToggle={toggleWord} label={`${wr.groupStudying} · ${studyingWords.length}`} rowState={studyingRowState} />
          )}
          {functionWords.length > 0 && (
            <BelowLevelGroup words={functionWords} selected={selected} onToggle={toggleWord} label={`${wr.groupFunctionWords} · ${functionWords.length}`} />
          )}
          {nameWords.length > 0 && (
            <BelowLevelGroup words={nameWords} selected={selected} onToggle={toggleWord} label={`${wr.groupNames} · ${nameWords.length}`} />
          )}

          {/* ── Daily queue toggle card ──────────────────────────────────────── */}
          {!(state?.targetDeckId) && (
            <button
              type="button"
              onClick={() => setAddToQueue((v) => !v)}
              className="flex items-center text-left w-full transition-colors"
              style={{
                gap: 14,
                padding: '14px 16px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              <div className="flex-1 min-w-0">
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {wr.addToQueue}
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 1 }}>
                  {wr.addToQueueSub}
                </p>
              </div>
              {/* Toggle switch */}
              <div
                aria-hidden="true"
                style={{
                  width: 46, height: 28,
                  borderRadius: 'var(--radius-pill)',
                  flexShrink: 0,
                  background: addToQueue ? 'var(--accent)' : 'var(--border)',
                  position: 'relative', transition: 'background 0.2s',
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, left: addToQueue ? 21 : 3,
                  width: 22, height: 22, borderRadius: '50%', background: 'white',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                }} />
              </div>
            </button>
          )}
        </div>
      </div>

      {/* ── Sticky footer CTA ─────────────────────────────────────────────────── */}
      <div
        ref={footerRef}
        className="wr-footer"
        style={{
          background: 'var(--bg-page)',
          borderTop: '1px solid var(--border)',
          boxShadow: '0 -8px 20px rgba(45,52,54,0.05)',
          padding: '12px 16px 16px',
          zIndex: 10,
        }}
      >
        <div className="max-w-2xl mx-auto">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || createDeck.isPending}
            className="w-full flex items-center justify-center gap-2 rounded-full font-semibold text-base
                       transition-all duration-150 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              minHeight: 52,
              padding: '8px 18px',
              background: selectedCount === 0 || createDeck.isPending ? 'var(--bg-secondary)' : 'var(--accent-fill)',
              color: selectedCount === 0 || createDeck.isPending ? 'var(--text-secondary)' : 'var(--text-on-accent)',
              border: selectedCount === 0 || createDeck.isPending ? '1px dashed var(--border)' : 'none',
              boxShadow: selectedCount === 0 || createDeck.isPending ? 'none' : 'var(--shadow-accent)',
            }}
          >
            {createDeck.isPending ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 animate-spin shrink-0"
                     style={{ borderColor: 'var(--text-on-dark)', borderTopColor: 'transparent' }} />
                {wr.creating}
              </>
            ) : state?.targetDeckId ? (
              wr.addToDeck(selectedCount)
            ) : (
              wr.createAndLearn(selectedCount)
            )}
          </button>
        </div>
      </div>
    </>
  )
}

// ── WordSection ───────────────────────────────────────────────────────────────

function WordSection({
  title,
  count,
  words,
  selected,
  onToggle,
  dimLevel,
}: {
  title: string
  count: number
  words: WordCandidateResponse[]
  selected: Set<string>
  onToggle: (lemma: string) => void
  dimLevel: 0 | 1 | 2
}) {
  const headerColor = dimLevel === 0 ? 'var(--text-primary)' : 'var(--text-secondary)'
  const headerOpacity = dimLevel === 2 ? 0.7 : 1

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 px-1" style={{ opacity: headerOpacity }}>
        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', color: headerColor }}>
          {title}
        </span>
        <span
          className="tabular-nums"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600,
            color: 'var(--text-secondary)', background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-pill)',
            minWidth: 22, height: 20, padding: '0 7px',
          }}
        >
          {count}
        </span>
      </div>
      <ul className="flex flex-col rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}>
        {words.map((word, idx) => (
          <WordRow
            key={word.lemma}
            word={word}
            isChecked={selected.has(word.lemma)}
            isLast={idx === words.length - 1}
            onToggle={() => onToggle(word.lemma)}
          />
        ))}
      </ul>
    </div>
  )
}

// ── BelowLevelGroup (legacy two-section, collapsible) ────────────────────────

function BelowLevelGroup({
  words,
  selected,
  onToggle,
  label,
  rowState,
}: {
  words: WordCandidateResponse[]
  selected: Set<string>
  onToggle: (lemma: string) => void
  label: string
  rowState?: (word: WordCandidateResponse) => RowState
}) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors"
        style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', minHeight: 44 }}
        aria-expanded={expanded}
      >
        <span className="text-sm font-medium">{label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', opacity: 0.5 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {expanded && (
        <ul className="flex flex-col" style={{ borderTop: '1px solid var(--border)' }}>
          {words.map((word, idx) => (
            <WordRow key={word.lemma} word={word} isChecked={selected.has(word.lemma)} isLast={idx === words.length - 1} onToggle={() => onToggle(word.lemma)} state={rowState?.(word)} />
          ))}
        </ul>
      )}
    </div>
  )
}

// ── WordRow ───────────────────────────────────────────────────────────────────

/** A row that is disabled cannot be selected; the note says why (or what selecting does). */
interface RowState {
  disabled: boolean
  note: string
}

function WordRow({
  word,
  isChecked,
  isLast,
  onToggle,
  state,
}: {
  word: WordCandidateResponse
  isChecked: boolean
  isLast: boolean
  onToggle: () => void
  state?: RowState
}) {
  const disabled = state?.disabled ?? false
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className="w-full flex items-start gap-3 text-left transition-colors"
        style={{
          padding: '11px 14px',
          background: 'var(--bg-surface)',
          borderBottom: isLast ? 'none' : '1px solid var(--border)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        aria-pressed={isChecked}
      >
        {/* Checkbox */}
        <span
          className="shrink-0 flex items-center justify-center"
          style={{
            width: 24, height: 24, borderRadius: 7,
            border: `2px solid ${isChecked ? 'var(--accent-fill)' : 'var(--border)'}`,
            background: isChecked ? 'var(--accent-fill)' : 'var(--bg-surface)',
            transition: 'background 120ms, border-color 120ms',
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          {isChecked && (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L20 6" stroke="white" strokeWidth="3.2" />
            </svg>
          )}
        </span>

        {/* Word content — two-line layout */}
        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 2 }}>
          {/* Line 1: word + CEFR chip + POS — baseline aligned */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="font-semibold leading-tight"
              style={{
                fontSize: 19,
                color: isChecked ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontFamily: 'var(--font-content)',
              }}
            >
              {word.word}
            </span>
            <CEFRBadge level={word.cefr_level} size="sm" />
            {word.pos && (
              <span
                className="uppercase"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  fontStyle: 'italic',
                  letterSpacing: '0.06em',
                  color: 'var(--text-secondary)',
                }}
              >
                {word.pos}
              </span>
            )}
          </div>
          {/* Line 2: translation — serif italic, only when present */}
          {word.translation && (
            <span
              className="leading-tight"
              style={{
                fontSize: 14,
                fontFamily: 'var(--font-content)',
                fontStyle: 'italic',
                color: 'var(--text-secondary)',
              }}
            >
              {word.translation}
            </span>
          )}
          {state && (
            <span className="leading-snug" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {state.note}
            </span>
          )}
        </div>
      </button>
    </li>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatCell({
  value,
  label,
  accent,
  muted,
  bordered,
}: {
  value: number
  label: string
  accent?: boolean
  muted?: boolean
  bordered?: boolean
}) {
  const numColor = accent ? 'var(--accent)' : muted ? 'var(--text-secondary)' : 'var(--text-primary)'
  return (
    <div
      style={{
        padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 1,
        ...(bordered ? { borderLeft: '1px solid var(--border)' } : {}),
      }}
    >
      <div
        className="tabular-nums"
        style={{
          fontFamily: 'var(--font-content)',
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: '-0.01em',
          color: numColor,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
        {label}
      </div>
    </div>
  )
}

function PageHeader({
  title,
  sourceUrl,
  sourceTitle,
  sourceFilename,
  onBack,
}: {
  title: string | null
  sourceUrl?: string | null
  sourceTitle?: string | null
  sourceFilename?: string | null
  onBack: () => void
}) {
  const { tr } = useLocale()
  const wr = tr.wordReview
  let sourceHost = ''
  if (sourceUrl) {
    try { sourceHost = new URL(sourceUrl).hostname.replace(/^www\./, '') } catch { /* ignore */ }
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium w-fit transition-opacity hover:opacity-70 mb-1 -mx-2 px-2"
        style={{ color: 'var(--text-secondary)', minHeight: 44 }}
      >
        <ArrowLeft size={15} />
        {wr.back}
      </button>
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
        {wr.reviewWords}
      </h1>
      {title ? (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{title}</p>
      ) : (
        // No title was typed on the previous screen — say plainly, before
        // Create is pressed, that a name is coming from somewhere else,
        // rather than silently deciding one and presenting it as chosen.
        <p className="text-sm italic" style={{ color: 'var(--text-secondary)' }}>
          {sourceTitle ? wr.autoNamedFrom(sourceTitle) : wr.autoNamedNotice}
        </p>
      )}
      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs w-fit mt-0.5 transition-opacity hover:opacity-70"
          style={{ color: 'var(--accent)' }}
        >
          <ExternalLink size={11} />
          {wr.fromSource(sourceTitle ?? sourceHost)}
        </a>
      )}
      {!sourceUrl && sourceFilename && (
        <p className="flex items-center gap-1 text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
          <File size={11} />
          {wr.fromSource(sourceFilename!)}
        </p>
      )}
    </div>
  )
}
